/**
 * AdminBanner — FE-W-11 (Auditor M-1).
 *
 * 회사 가입 직후 24h grace 기간 안에 표시되는 상단 안내 banner.
 * graceUntil 경과 시 자동 미표시. 24h 안에 ADMIN 의 sensitive action (사용자 초대, 권한 변경,
 * 데이터 export 등) 이 backend 의 assertNotInGraceOrFrozen 으로 차단됨을 사용자에게 알림 +
 * 도메인 owner 가 본인이 아닌 경우 운영팀에 이의 제기 동선 제공.
 *
 * 환경변수:
 *   - VITE_OPERATOR_CONTACT_URL — 이의 제기 form/email URL.
 *     미설정 시 button 미표시 (안내 텍스트만).
 *
 * 본 cycle 한계 (Auditor D-2 backlog):
 *   - graceUntil 은 onboardingStore 에서만 — 다른 탭 / 새로고침 시 잃음.
 *   - 다음 cycle 에서 /users/me 응답에 institution.graceUntil 포함 + AdminBanner 가
 *     auth probe 결과로 표시 (영속).
 *
 * Cycle 72: i18n 적용 (shared-i18n.ts 확장). 7 hardcoded → tShared.
 */

import { useEffect, useState, useSyncExternalStore } from 'react';
import { useOnboardingStore } from '../store/onboardingStore';
import { useAuthStore } from '../store/authStore';
import { tShared, getSharedComponentLang, subscribeSharedComponentLang } from '../lib/shared-i18n';

function formatRemaining(untilMs: number, now: number): string {
  const diff = untilMs - now;
  if (diff <= 0) return '';
  const h = Math.floor(diff / (60 * 60 * 1000));
  const m = Math.floor((diff % (60 * 60 * 1000)) / (60 * 1000));
  return h > 0
    ? tShared('shared.admin_banner.remaining_h_m', { h, m })
    : tShared('shared.admin_banner.remaining_m', { m });
}

export function AdminBanner() {
  useSyncExternalStore(subscribeSharedComponentLang, getSharedComponentLang, getSharedComponentLang);
  const graceUntilStr = useOnboardingStore((s) => s.graceUntil);
  const user = useAuthStore((s) => s.user);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!graceUntilStr) return;
    const timer = setInterval(() => setNow(Date.now()), 60_000); // 분당 갱신
    return () => clearInterval(timer);
  }, [graceUntilStr]);

  // ADMIN 만 표시 — 일반 사용자에게 무의미.
  if (!user || user.role !== 'ADMIN') return null;
  if (!graceUntilStr) return null;

  const untilMs = new Date(graceUntilStr).getTime();
  if (!Number.isFinite(untilMs) || untilMs <= now) return null;

  const remaining = formatRemaining(untilMs, now);
  const contactUrl = import.meta.env.VITE_OPERATOR_CONTACT_URL as string | undefined;

  return (
    <div
      role="status"
      aria-live="polite"
      className="admin-grace-banner"
      style={{
        padding: '0.75rem 1.25rem',
        background: 'var(--color-info-bg, #eff6ff)',
        borderBottom: '1px solid var(--color-info, #93c5fd)',
        color: 'var(--color-info-text, #1e40af)',
        fontSize: '0.875rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '1rem',
        flexWrap: 'wrap',
      }}
    >
      <div>
        <strong>{tShared('shared.admin_banner.title')}</strong> ·{' '}
        {tShared('shared.admin_banner.body', { remaining })}
      </div>
      {contactUrl && (
        <a
          href={contactUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            color: 'var(--color-info-text, #1e40af)',
            fontWeight: 600,
            textDecoration: 'underline',
            whiteSpace: 'nowrap',
          }}
          aria-label={tShared('shared.admin_banner.contact_aria')}
        >
          {tShared('shared.admin_banner.contact_label')}
        </a>
      )}
    </div>
  );
}
